<template>
  <h1>Colaboradores asignados</h1>
  <p class="mt-2 text-800 font-medium">
    Lista de empleados asignados para su supervisión.
  </p>
  <div class="card p-fluid pt-3">
    <DataTable
      ref="dt"
      :value="employees"
      editMode="row"
      dataKey="id"
      paginator
      :rows="5"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      tableClass="editable-cells-table"
      tableStyle="min-width: 50rem"
      :exportFilename="`EmpleadosAsignados_${dayjs().format('YYYY-MM-DD')}`"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      :currentPageReportTemplate="currentPageReportTemplate"
      :totalRecords="employees?.length"
      scrollHeight="600px"
      scrollable
      class="tabla"
      v-model:filters="filters"
      filterDisplay="row"
      :globalFilterFields="['code', 'name', 'supervisor.name']"
    >
      <template #header>
        <div class="flex justify-content-end">
          <Button
            icon="pi pi-file-excel"
            label="Exportar"
            @click="exportCSV()"
            class="text-sm font-light bg-green-100 border-green-100 text-green-700 mr-3"
            style="width: 120px"
          />
          <span class="p-input-icon-left w-3">
            <i class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Buscar" />
          </span>
        </div>
      </template>
      <Column header="Nómina">
        <template #body="{ data, field }">
          {{ data.payroll.code }} - {{ data.payroll.name }}
        </template>
      </Column>
      <Column header="Empleado" field="name" style="width: 20%">
        <template #body="{ data }">
          {{ data.code }} - {{ data.name }}
        </template>
      </Column>
      <Column header="Fecha de ingreso" field="joiningDate" style="width: 20%">
        <template #body="{ data, field }">
          <span v-basic-date="data[field]" v-if="data[field]"></span>
          <span class="text-600 font-medium" v-else>Sin definir</span>
        </template>
      </Column>
      <Column
        header="Saldo de vacaciones"
        field="vacationBalance"
        style="width: 20%"
      >
        <template #body="{ data, field }">
          <span v-if="data.vacationBalance">{{
            Number(data[field]).toFixed(2)
          }}</span>
          <span class="text-600 font-medium" v-else>Sin definir</span>
        </template>
      </Column>
      <Column header="Area" field="area" style="width: 20%">
        <template #body="{ data, field }">
          <span v-if="data.area">{{ data[field] }}</span>
          <span class="text-600 font-medium" v-else>Sin definir</span>
        </template>
      </Column>
      <Column header="Departamento" field="department" style="width: 20%">
        <template #body="{ data, field }">
          <span v-if="data.department">{{ data[field] }}</span>
          <span class="text-600 font-medium" v-else>Sin definir</span>
        </template>
      </Column>
      <Column header="Dirección" field="direction" style="width: 20%">
        <template #body="{ data, field }">
          <span v-if="data.direction">{{ data[field] }}</span>
          <span class="text-600 font-medium" v-else>Sin definir</span>
        </template>
      </Column>

      <template #empty> No se encontraron empleados asignados. </template>
      <template #loading> Cargando informacion de los empleados... </template>
    </DataTable>
  </div>
</template>
<script setup lang="ts">
  import { getEmployeesByApproverId } from '@/modules/employees/services/EmployeeService'
  import { useUserStore } from '@/store'
  import useLoading from '@/store/load'
  import dayjs from 'dayjs'
  import { storeToRefs } from 'pinia'
  import { FilterMatchMode } from 'primevue/api'
  import DataTable from 'primevue/datatable'
  import { assignedEmployeesReportXlsx } from '@/shared/utils/reports/assingedReportXlsx'
  import { ref, computed, onMounted } from 'vue'
  import { SubordinatedEmployeeDto } from '@/modules/employees/models/SubordinatedEmployee'
  const { user } = storeToRefs(useUserStore())
  const loadingStore = useLoading()
  const dt = ref()

  const employees = ref<SubordinatedEmployeeDto[]>()
  onMounted(async () => {
    loadingStore.setLoadingState()
    const employeesResponse = await getEmployeesByApproverId(
      user.value?.id || ''
    )
    employees.value = employeesResponse
    loadingStore.dismissLoadingState()
  })

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    supervisor: { value: null, matchMode: FilterMatchMode.IN },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    code: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  })

  const exportCSV = () => {
    assignedEmployeesReportXlsx(employees.value || [])
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${employees.value?.length}`
  })
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
